import React, { useEffect, useState } from "react";

import { Box, CircularProgress, Grid } from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import { useParams } from "react-router-dom";

import { getContainerReport } from "api/receipt";

import { StatusLabel } from "components/Custom";
import PageTitle from "components/PageTitle";
import DefaultTable from "components/Table";
import Widget from "components/Widget";

import { useLayoutDispatch, popupNotification } from "context/LayoutContext";
import { exportToExcel } from "utils/exportToExcel";
import { convertDataToTableView } from "utils/convert";

const STATUS_MAP = {
  overage: "Warning",
  shortage: "Cancelled",
  matched: "Completed",
};

const lineHeaders = [
  { id: "sku", label: "product_sku", sortable: false },
  { id: "expected", sortable: false },
  { id: "transferred", sortable: false },
  {
    id: "difference",
    sortable: false,
    render: (r, exported) =>
      exported ? (
        r.difference
      ) : (
        <StatusLabel
          status={STATUS_MAP[r.status] || ""}
          text={`${r.difference > 0 ? "+" : ""}${r.difference}`}
          altText={r.status}
        />
      ),
  },
];

const buggyHeaders = [
  { id: "sku", label: "product_sku", sortable: false },
  { id: "location", sortable: false },
  { id: "qty", sortable: false },
];

export default function ContainerReport() {
  const { id } = useParams();
  const layoutDispatch = useLayoutDispatch();
  const [isFetching, setIsFetching] = useState(true);
  const [report, setReport] = useState(null);
  const [exportDisabled, setExportDisabled] = useState(false);

  useEffect(() => {
    getContainerReport({
      params: { receipt_id: id },
      responseSetter: (res) => {
        setIsFetching(false);
        if (res.success) {
          setReport(res);
        } else {
          popupNotification({
            dispatch: layoutDispatch,
            message: res.message || "Failed to load report.",
            status: "error",
          });
        }
      },
      setError: () => setIsFetching(false),
    });
  }, [id, layoutDispatch]);

  const exportLines = () => {
    exportToExcel(
      [],
      `ContainerReport-${report?.receipt?.number}`,
      true,
      convertDataToTableView(report.lines, lineHeaders),
      setExportDisabled,
    );
  };

  const exportBuggies = () => {
    exportToExcel(
      [],
      `ContainerBuggies-${report?.receipt?.number}`,
      true,
      convertDataToTableView(report.buggies, buggyHeaders),
      setExportDisabled,
    );
  };

  const receiptNumber = report?.receipt?.number || "-";

  return (
    <>
      <PageTitle
        title={`Container Report ${receiptNumber}`}
        backLink={"/app/receipt"}
      />
      {isFetching ? (
        <CircularProgress />
      ) : !report ? (
        <Alert severity="warning">No report found.</Alert>
      ) : (
        <Grid container spacing={4}>
          <Grid item xs={12}>
            <Widget>
              <Grid container spacing={2}>
                <Summary label="Container" value={report.receipt.container} />
                <Summary label="Dock" value={report.receipt.dock_id} />
                <Summary label="Status" value={report.receipt.status} />
                <Summary label="Expected" value={report.totals.expected} />
                <Summary label="Transferred" value={report.totals.transferred} />
                <Summary
                  label="Overage"
                  value={`+${report.totals.overage}`}
                  color="#1976d2"
                />
                <Summary
                  label="Shortage"
                  value={`-${report.totals.shortage}`}
                  color="#d32f2f"
                />
              </Grid>
            </Widget>
          </Grid>
          <Grid item lg={6} md={12} sm={12} xs={12}>
            <Box fontSize="1.5rem" fontWeight="bold">
              Overages / Shortages
            </Box>
            <DefaultTable
              headers={lineHeaders}
              data={{
                items: report.lines,
                isFetching: false,
                count: report.lines.length,
              }}
              handleExportData={exportLines}
              exportDisabled={exportDisabled}
              setExportDisabled={setExportDisabled}
            />
          </Grid>
          <Grid item lg={6} md={12} sm={12} xs={12}>
            <Box fontSize="1.5rem" fontWeight="bold">
              Buggies Used
            </Box>
            <DefaultTable
              headers={buggyHeaders}
              data={{
                items: report.buggies,
                isFetching: false,
                count: report.buggies.length,
              }}
              handleExportData={exportBuggies}
              exportDisabled={exportDisabled}
              setExportDisabled={setExportDisabled}
            />
          </Grid>
        </Grid>
      )}
    </>
  );
}

function Summary({ label, value, color }) {
  return (
    <Grid item xs={6} sm={4} md={3} lg={2}>
      <Box fontSize="0.8rem" color="textSecondary">
        {label}
      </Box>
      <Box fontSize="1.3rem" fontWeight="bold" style={color ? { color } : {}}>
        {value ?? "-"}
      </Box>
    </Grid>
  );
}
